import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaDownload } from "react-icons/fa";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import {
  NegotiationRoundStatus,
  Populater,
  useGetProductGroupsForSourcingEventQuery,
  useListBidV2SForEventQuery,
  useSelectNegotiationRoundV2ProductBidsMutation,
  useSelectSourcingEventProductBidsMutation,
  type ProductDto,
  type ProductGroupDto,
  type ReceivedBidV2Dto,
  type SourcingEventV2Dto,
  type WinningProductGroupBidsDto,
} from "../../../../autogen/bff-api";
import { useCreateEditor } from "../../../../common/editing/save-coordinator/useCreateEditor";
import { useEditor } from "../../../../common/editing/save-coordinator/useEditor";
import { useApiError } from "../../../../common/errors/useApiError";
import { ProductFieldCell } from "../../../../common/products/ProductFieldCell";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { generateProductEvaluationReport, generateProductPriceComparisonReport } from "./productBidReports";

// The product basket is a whole-array replace on every price click. Routing it through the save
// coordinator makes rapid clicks compose against the optimistic basket and serialises the saves,
// so two quick clicks can no longer race each other into a lost or doubled selection.
interface ProductSelectionState extends Record<string, unknown> {
  selectedProductBids: WinningProductGroupBidsDto[];
}

export const ProductEvaluationSection = ({
  event,
  readOnly,
}: {
  event: SourcingEventV2Dto;
  readOnly: boolean;
}) => {
  const { trigger } = useApiError();
  const { selectedRound } = useRoundSelection();
  const { data: groupsData } = useGetProductGroupsForSourcingEventQuery({ sourcingEventId: event.id });
  const { data: bidsData } = useListBidV2SForEventQuery({ eventId: event.id });
  const [selectEventProductBids] = useSelectSourcingEventProductBidsMutation();
  const [selectRoundProductBids] = useSelectNegotiationRoundV2ProductBidsMutation();

  const roundId = selectedRound?.id;
  const rawSelection = selectedRound ? selectedRound.selectedProductBids : event.selectedProductBids;
  const entityId = `product-selection:${event.id}:${roundId ?? "original"}`;
  const initialState = useMemo<ProductSelectionState>(
    () => ({ selectedProductBids: rawSelection ?? [] }),
    [rawSelection],
  );
  const save = useCallback(
    (fields: Partial<ProductSelectionState>) => {
      const next = fields.selectedProductBids ?? [];
      const request = { selectProductBidsRequest: { selectedProductBids: next } };
      const promise = roundId
        ? selectRoundProductBids({ eventId: event.id, roundId, ...request })
        : selectEventProductBids({ eventId: event.id, ...request });
      return promise.unwrap().then((r) => ({ selectedProductBids: r.selectedProductBids ?? [] }));
    },
    [event.id, roundId, selectEventProductBids, selectRoundProductBids],
  );

  useCreateEditor<ProductSelectionState>(entityId, { initialState, save, onError: (e) => trigger(e) });

  const groups = groupsData?.productGroups ?? [];
  // ADR-033 D2/D4: the matrix and the basket belong to the selected competition stage.
  const bids = (bidsData?.bids ?? [])
    .filter((bid) =>
      selectedRound ? bid.forNegotiationRound === selectedRound.id : bid.forNegotiationRound == null,
    )
    .filter((bid) => bid.products?.length);
  if (groups.length === 0 || bids.length === 0) return null;

  return (
    <ProductEvaluationBody
      entityId={entityId}
      event={event}
      groups={groups}
      bids={bids}
      originalBids={(bidsData?.bids ?? []).filter((bid) => bid.forNegotiationRound == null)}
      isPrivate={event.eventContext.type === "Private"}
      frozen={selectedRound ? selectedRound.status === NegotiationRoundStatus.Completed || readOnly : readOnly}
      inRoundContext={selectedRound != null}
    />
  );
};

const ProductEvaluationBody = ({
  entityId,
  event,
  groups,
  bids,
  originalBids,
  isPrivate,
  frozen,
  inRoundContext,
}: {
  entityId: string;
  event: SourcingEventV2Dto;
  groups: ProductGroupDto[];
  bids: ReceivedBidV2Dto[];
  originalBids: ReceivedBidV2Dto[];
  isPrivate: boolean;
  frozen: boolean;
  inRoundContext: boolean;
}) => {
  const { t } = useTranslation();
  const { state, updateField } = useEditor<ProductSelectionState>(entityId);
  const selection = state.selectedProductBids ?? [];
  const hasSelection = selection.some((group) => group.bids.some((product) => product.bids.length > 0));

  const selectedBidsFor = (groupId: string, productId: string): string[] =>
    selection
      .find((group) => group.productGroupId === groupId)
      ?.bids.find((product) => product.productId === productId)?.bids ?? [];

  const buildSelection = (
    mutate: (groupId: string, productId: string, current: string[]) => string[],
  ): WinningProductGroupBidsDto[] =>
    groups.map((group) => ({
      productGroupId: group.id,
      bids: group.products.map((product) => ({
        productId: product.id,
        bids: mutate(group.id, product.id, selectedBidsFor(group.id, product.id)),
      })),
    }));

  const toggle = (groupId: string, productId: string, bidId: string) =>
    updateField({
      selectedProductBids: buildSelection((gId, pId, current) => {
        if (gId !== groupId || pId !== productId) return current;
        return current.includes(bidId) ? current.filter((id) => id !== bidId) : [...current, bidId];
      }),
    });

  const selectAllFromSupplier = (group: ProductGroupDto, bid: ReceivedBidV2Dto) =>
    updateField({
      selectedProductBids: buildSelection((gId, pId, current) => {
        if (gId !== group.id) return current;
        const product = group.products.find((p) => p.id === pId);
        return product && totalPriceFor(bid, group, product) != null ? [bid.id] : current;
      }),
    });

  const selectEverythingFromSupplier = (bid: ReceivedBidV2Dto) =>
    updateField({
      selectedProductBids: buildSelection((gId, pId, current) => {
        const group = groups.find((g) => g.id === gId);
        const product = group?.products.find((p) => p.id === pId);
        return group && product && totalPriceFor(bid, group, product) != null ? [bid.id] : current;
      }),
    });

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" pb="1">
        <Heading size="sm">{t("Product prices")}</Heading>
        <Flex gap="2">
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            rightIcon={<Icon as={FaDownload} />}
            onClick={() => generateProductPriceComparisonReport(groups, bids, event)}
          >
            {t("Download all prices")}
          </Button>
          <Explanation
            text={t("Select winning prices in the table first")}
            enabled={!hasSelection}
            shouldWrapChildren
          >
            <Button
              size="sm"
              colorScheme="teal"
              variant="outline"
              rightIcon={<Icon as={FaDownload} />}
              isDisabled={!hasSelection}
              onClick={() => generateProductEvaluationReport(groups, bids, event, selection)}
            >
              {t("Selected product prices")}
            </Button>
          </Explanation>
        </Flex>
      </Flex>
      <Text fontSize="sm" color="smMuted" pb="3">
        {t("Compare total prices per product and click the price that wins each product.")}
      </Text>
      {isPrivate && (
        <CrossGroupSummary
          groups={groups}
          bids={bids}
          originalBids={originalBids}
          inRoundContext={inRoundContext}
          frozen={frozen}
          selectedBidsFor={selectedBidsFor}
          onSelectEverything={selectEverythingFromSupplier}
        />
      )}
      {groups.map((group) => (
        <GroupEvaluationTable
          key={group.id}
          group={group}
          bids={bids}
          readOnly={frozen}
          selectedBidsFor={(productId) => selectedBidsFor(group.id, productId)}
          onToggle={(productId, bidId) => toggle(group.id, productId, bidId)}
          onSelectAllFromSupplier={(bid) => selectAllFromSupplier(group, bid)}
        />
      ))}
    </Box>
  );
};

/**
 * V1's cross-group shopping summary (private events): totals per supplier with
 * take-everything on click, the best-possible vs currently shopped basket, quote
 * coverage and — in a round — the change against each supplier's original bid.
 */
const CrossGroupSummary = ({
  groups,
  bids,
  originalBids,
  inRoundContext,
  frozen,
  selectedBidsFor,
  onSelectEverything,
}: {
  groups: ProductGroupDto[];
  bids: ReceivedBidV2Dto[];
  originalBids: ReceivedBidV2Dto[];
  inRoundContext: boolean;
  frozen: boolean;
  selectedBidsFor: (groupId: string, productId: string) => string[];
  onSelectEverything: (bid: ReceivedBidV2Dto) => void;
}) => {
  const { t } = useTranslation();

  const totalFor = (bid: ReceivedBidV2Dto): number =>
    groups.reduce(
      (sum, group) =>
        sum + group.products.reduce((groupSum, product) => groupSum + (totalPriceFor(bid, group, product) ?? 0), 0),
      0,
    );

  const bestPriceFor = (group: ProductGroupDto, product: ProductDto): number | null => {
    const prices = bids
      .map((bid) => totalPriceFor(bid, group, product))
      .filter((price): price is number => price != null);
    return prices.length ? Math.min(...prices) : null;
  };

  const sumOfBest = groups.reduce(
    (sum, group) =>
      sum + group.products.reduce((groupSum, product) => groupSum + (bestPriceFor(group, product) ?? 0), 0),
    0,
  );

  const sumOfSelected = groups.reduce(
    (sum, group) =>
      sum +
      group.products.reduce((groupSum, product) => {
        const selected = selectedBidsFor(group.id, product.id)
          .map((bidId) => bids.find((bid) => bid.id === bidId))
          .map((bid) => (bid ? totalPriceFor(bid, group, product) : null))
          .filter((price): price is number => price != null);
        return groupSum + selected.reduce((s, price) => s + price, 0);
      }, 0),
    0,
  );

  const quotesRequested = groups.reduce((sum, group) => sum + group.products.length, 0);
  const quotesFor = (bid: ReceivedBidV2Dto): number =>
    groups.reduce(
      (sum, group) =>
        sum + group.products.filter((product) => totalPriceFor(bid, group, product) != null).length,
      0,
    );

  const originalTotalFor = (bid: ReceivedBidV2Dto): number | null => {
    const original = originalBids.find((candidate) => candidate.owner === bid.owner);
    if (!original) return null;
    const total = totalFor(original);
    return total > 0 ? total : null;
  };

  const bestTotal = Math.min(...bids.map(totalFor).filter((total) => total > 0));

  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="lg" mb="6" p="4" overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th p="1"></Th>
            {bids.map((bid) => (
              <Th key={bid.id} p="1" textAlign="right">
                {bid.ownerName}
              </Th>
            ))}
            <Th p="1" textAlign="right">{t("Best prices")}</Th>
            <Th p="1" textAlign="right">{t("Selected")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr fontWeight="bold">
            <Td p="1">{t("SUM Total")}</Td>
            {bids.map((bid) => {
              const total = totalFor(bid);
              return (
                <Td
                  key={bid.id}
                  p="1"
                  textAlign="right"
                  cursor={frozen ? "default" : "pointer"}
                  color={total > 0 && total === bestTotal ? "green.500" : "yellow.600"}
                  title={frozen ? undefined : t("Select everything from this supplier") ?? undefined}
                  onClick={frozen ? undefined : () => onSelectEverything(bid)}
                >
                  {total.toLocaleString()}
                </Td>
              );
            })}
            <Td p="1" textAlign="right">{sumOfBest.toLocaleString()}</Td>
            <Td p="1" textAlign="right">{sumOfSelected.toLocaleString()}</Td>
          </Tr>
          {inRoundContext && (
            <Tr>
              <Td p="1">{t("Change since original bid")}</Td>
              {bids.map((bid) => {
                const original = originalTotalFor(bid);
                const total = totalFor(bid);
                if (original == null || total <= 0) {
                  return (
                    <Td key={bid.id} p="1" textAlign="right" color="smMuted" fontSize="xs">
                      {t("n/a")}
                    </Td>
                  );
                }
                const percent = ((total - original) / original) * 100;
                return (
                  <Td
                    key={bid.id}
                    p="1"
                    textAlign="right"
                    fontSize="xs"
                    color={percent > 0 ? "smError" : percent < 0 ? "smSuccess" : "smMuted"}
                  >
                    {`${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`}
                  </Td>
                );
              })}
              <Td p="1"></Td>
              <Td p="1"></Td>
            </Tr>
          )}
          <Tr>
            <Td p="1">{t("Product quotes")}</Td>
            {bids.map((bid) => (
              <Td
                key={bid.id}
                p="1"
                textAlign="right"
                color={quotesFor(bid) >= quotesRequested ? "green.500" : "yellow.600"}
              >
                {quotesFor(bid)}/{quotesRequested}
              </Td>
            ))}
            <Td p="1"></Td>
            <Td p="1"></Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
};

const GroupEvaluationTable = ({
  group,
  bids,
  readOnly,
  selectedBidsFor,
  onToggle,
  onSelectAllFromSupplier,
}: {
  group: ProductGroupDto;
  bids: ReceivedBidV2Dto[];
  readOnly: boolean;
  selectedBidsFor: (productId: string) => string[];
  onToggle: (productId: string, bidId: string) => void;
  onSelectAllFromSupplier: (bid: ReceivedBidV2Dto) => void;
}) => {
  const { t } = useTranslation();
  const buyerFields = group.productFields.filter((field) => field.populatedBy === Populater.Buyer);

  const bestPriceFor = (product: ProductDto): number | null => {
    const prices = bids
      .map((bid) => totalPriceFor(bid, group, product))
      .filter((price): price is number => price != null);
    return prices.length ? Math.min(...prices) : null;
  };

  const selectedPriceFor = (product: ProductDto): number | null => {
    const selected = selectedBidsFor(product.id);
    const prices = selected
      .map((bidId) => bids.find((bid) => bid.id === bidId))
      .map((bid) => (bid ? totalPriceFor(bid, group, product) : null))
      .filter((price): price is number => price != null);
    return prices.length ? prices.reduce((sum, price) => sum + price, 0) : null;
  };

  const groupTotalFor = (bid: ReceivedBidV2Dto): number =>
    group.products.reduce((sum, product) => sum + (totalPriceFor(bid, group, product) ?? 0), 0);

  const sumOfBest = group.products.reduce((sum, product) => sum + (bestPriceFor(product) ?? 0), 0);
  const sumOfSelected = group.products.reduce((sum, product) => sum + (selectedPriceFor(product) ?? 0), 0);

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor="teal.100"
      rounded="lg"
      mb="8"
      p="5"
    >
      <Text fontWeight="bold" pb="4" pl="1">
        {group.name}
      </Text>
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              {buyerFields.map((field) => (
                <Th key={field.id} p="1">
                  {field.name}
                </Th>
              ))}
              <Th p="1">{t("Quantity")}</Th>
              {bids.map((bid) => (
                <Th
                  key={bid.id}
                  p="1"
                  textAlign="right"
                  cursor={readOnly ? "default" : "pointer"}
                  onClick={readOnly ? undefined : () => onSelectAllFromSupplier(bid)}
                >
                  {bid.ownerName}
                </Th>
              ))}
              <Th p="1" textAlign="right">
                {t("Best Price")}
              </Th>
              <Th p="1" textAlign="right">
                {t("Selected Price")}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {group.products.map((product) => (
              <EvaluationProductRow
                key={product.id}
                product={product}
                group={group}
                buyerFieldNames={buyerFields.map((field) => field.name)}
                bids={bids}
                readOnly={readOnly}
                selectedBidIds={selectedBidsFor(product.id)}
                bestPrice={bestPriceFor(product)}
                selectedPrice={selectedPriceFor(product)}
                onToggle={(bidId) => onToggle(product.id, bidId)}
              />
            ))}
            <Tr fontWeight="bold">
              <Td p="1" colSpan={buyerFields.length + 1}>
                SUM
              </Td>
              {bids.map((bid) => (
                <Td key={bid.id} p="1" textAlign="right">
                  {groupTotalFor(bid).toLocaleString()}
                </Td>
              ))}
              <Td p="1" textAlign="right">
                {sumOfBest.toLocaleString()}
              </Td>
              <Td p="1" textAlign="right">
                {sumOfSelected.toLocaleString()}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const EvaluationProductRow = ({
  product,
  group,
  buyerFieldNames,
  bids,
  readOnly,
  selectedBidIds,
  bestPrice,
  selectedPrice,
  onToggle,
}: {
  product: ProductDto;
  group: ProductGroupDto;
  buyerFieldNames: string[];
  bids: ReceivedBidV2Dto[];
  readOnly: boolean;
  selectedBidIds: string[];
  bestPrice: number | null;
  selectedPrice: number | null;
  onToggle: (bidId: string) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);
  const fieldByName = (name: string) => product.productFields.find((field) => field.name === name);

  return (
    <Tr>
      {buyerFieldNames.map((name) => {
        const field = fieldByName(name);
        return (
          <ProductFieldCell
            key={name}
            value={field?.value as string | number | boolean | null | undefined}
            isBoolean={field?.type === "Boolean"}
            isExpanded={isExpanded}
            onToggle={toggleExpanded}
            fadeToColor="smBackground"
            p="1"
          />
        );
      })}
      <Td p="1" verticalAlign="top">
        {product.quantity.value ?? "—"}
      </Td>
      {bids.map((bid) => {
        const price = totalPriceFor(bid, group, product);
        const isSelected = selectedBidIds.includes(bid.id);
        const isBest = price != null && price === bestPrice;
        return (
          <Td
            key={bid.id}
            data-testid={`product-price-${product.id}-${bid.id}`}
            textAlign="right"
            verticalAlign="top"
            p="1"
            cursor={!readOnly && price != null ? "pointer" : "default"}
            color={isBest ? "green.500" : "yellow.600"}
            fontWeight={isSelected ? "bold" : "normal"}
            onClick={() => {
              if (readOnly || price == null) return;
              onToggle(bid.id);
            }}
          >
            <Box
              display="inline-block"
              rounded="md"
              px="3"
              py="1"
              border={isSelected ? "1px solid" : "1px solid transparent"}
              borderColor={isSelected ? "smBorder" : "transparent"}
            >
              {price != null ? price.toLocaleString() : "—"}
            </Box>
          </Td>
        );
      })}
      <Td textAlign="right" verticalAlign="top" p="1">
        {bestPrice != null ? bestPrice.toLocaleString() : "—"}
      </Td>
      <Td textAlign="right" verticalAlign="top" fontWeight="bold" p="1">
        {selectedPrice != null ? selectedPrice.toLocaleString() : ""}
      </Td>
    </Tr>
  );
};

const totalPriceFor = (
  bid: ReceivedBidV2Dto,
  group: ProductGroupDto,
  product: ProductDto,
): number | null => {
  const unitPrice = bid.products
    ?.find((groupBid) => groupBid.productGroupId === group.id)
    ?.productBids.find((productBid) => productBid.productId === product.id)?.unitPrice.value;
  if (unitPrice == null) return null;
  const quantity = product.quantity.value;
  return quantity != null ? Number(unitPrice) * Number(quantity) : Number(unitPrice);
};
